import { engineComponent } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { RopeHeavenElement } from "./RopeHeavenElement";
import { Configuration } from "../Components/Configuration";
import { LayoutContainerElement } from "./LayoutContainerElement";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { PlayList } from "../Components/Sounds/PlayList";
import { LayoutManager } from "../Managers/LayoutManager";

@engineComponent(ComponentTypes.DisplayObject)
export class ClockCounter extends LayoutContainerElement {
    public static readonly RopeFinished: string = "eventSelectionFinished";

    private rope1: RopeHeavenElement;
    private rope2: RopeHeavenElement;
    private clocks: SwitchElement[] = [];

    private symbolX: number[] = [];
    private symbolY: number[] = [];

    private symbolXPortrait: number[] = [];
    private symbolYPortrait: number[] = [];

    private ropeTimer: FrameTimerEventEmitter;
    private ropeDelay: number = 120;
    private updateDelay: number = 60;

    private lastStartOptions: ClockCounterStartOptions;

    constructor() {
        super();
        this.ropeTimer = CreateFrameTimer("enableTimer");
        this.ropeTimer.on(
            FrameTimerEvents.Complete,
            function () {
                this.rope1.visible = false;
                this.rope2.visible = false;
                this.emit(ClockCounter.RopeFinished, this.lastStartOptions);
            }.bind(this),
        );
    }

    createByNode(node: Element) {
        super.createByNode(node);

        for (let key in this.childs) {
            if (key === "Rope1" && this.childs[key] instanceof RopeHeavenElement) {
                this.rope1 = this.childs[key];
                this.rope1.visible = false;
            }
            if (key === "Rope2" && this.childs[key] instanceof RopeHeavenElement) {
                this.rope2 = this.childs[key];
                this.rope2.visible = false;
            }
            if (this.childs[key] instanceof SwitchElement) {
                this.clocks.push(this.childs[key]);
            }
        }

        if (node.hasAttribute("Configs")) {
            let configs: Array<string> = node.getAttribute("Configs").split(";");
            for (let i: number = 0; i < configs.length; i++) {
                let config: Element = Configuration.Configurations[configs[i]];
                if (config != null) {
                    this.configurate(config);
                }
            }
        }
    }

    protected configurate(node: Element): void {
        if (node.hasAttribute("ReelX") && node.hasAttribute("SymbolWidth")) {
            let sW = parseInt(node.getAttribute("SymbolWidth"));
            node.getAttribute("ReelX")
                .split(";")
                .forEach((v) => this.symbolX.push(parseInt(v) + sW / 2));
        }
        if (
            node.hasAttribute("ReelY") &&
            node.hasAttribute("SymbolHeight") &&
            node.hasAttribute("VisibleSymbolCount")
        ) {
            let sH = parseInt(node.getAttribute("SymbolHeight"));
            let sC = parseInt(node.getAttribute("VisibleSymbolCount"));
            let offset = parseInt(node.getAttribute("ReelY").split(";")[0]);
            for (let i = 0; i < sC; i++) {
                this.symbolY.push(offset + i * sH + sH / 2);
            }
        }

        if (node.hasAttribute("ReelXPortrait") && node.hasAttribute("SymbolWidthPortrait")) {
            let sW = parseInt(node.getAttribute("SymbolWidthPortrait"));
            node.getAttribute("ReelXPortrait")
                .split(";")
                .forEach((v) => this.symbolXPortrait.push(parseInt(v) + sW / 2));
        }
        if (
            node.hasAttribute("ReelYPortrait") &&
            node.hasAttribute("SymbolHeightPortrait") &&
            node.hasAttribute("VisibleSymbolCount")
        ) {
            let sH = parseInt(node.getAttribute("SymbolHeightPortrait"));
            let sC = parseInt(node.getAttribute("VisibleSymbolCount"));
            let offset = parseInt(node.getAttribute("ReelYPortrait").split(";")[0]);
            for (let i = 0; i < sC; i++) {
                this.symbolYPortrait.push(offset + i * sH + sH / 2);
            }
        }

        if (node.hasAttribute("DelayCounter")) {
            this.ropeDelay = parseInt(node.getAttribute("DelayCounter"));
        }
        if (node.hasAttribute("DelayCounterUpdate")) {
            this.updateDelay = parseInt(node.getAttribute("DelayCounterUpdate"));
        }
    }

    public setValue(v: number, delay?: number, noPop?: boolean) {
        this.clocks.forEach((clock) => {
            if (v > 4) {
                clock.goToCase("TO_4_NOPOP", false, delay || 0);
            } else if (v <= 0) {
                clock.goToCase("TO_0", false);
            } else {
                clock.goToCase("TO_" + v + (noPop ? "_NOPOP" : ""), false, delay || 0);
            }
            v -= 4;
        });
    }

    public start(options: ClockCounterStartOptions) {
        this.setValue(options.currentClocks, this.updateDelay);

        this.lastStartOptions = options;

        if (options.currentClocks > 0) {
            let points: string[] = [];
            if (options.reelIndex >= this.symbolX.length) {
                this.logError(
                    "reelIndex is greater than symbolX length " + options.reelIndex + " " + this.symbolX.length,
                );
                return;
            }
            if (options.symbolIndex >= this.symbolY.length) {
                this.logError(
                    "symbolIndex is greater than symbolY length " + options.symbolIndex + " " + this.symbolY.length,
                );
                return;
            }
            let symbolX = this.symbolX[options.reelIndex];
            let symbolY = this.symbolY[options.symbolIndex];
            let dY = this.symbolY[1] - this.symbolY[0];

            if (LayoutManager.isPortrait) {
                symbolX = this.symbolXPortrait[options.reelIndex];
                symbolY = this.symbolYPortrait[options.symbolIndex];
                dY = this.symbolYPortrait[1] - this.symbolYPortrait[0];
            }

            this.clocks.forEach((clock) => {
                this.addChildAt(clock, 1);
            });
            let clockIndex = Math.ceil(options.currentClocks / 4) - 1;
            clockIndex = clockIndex > 3 ? 3 : clockIndex;
            let clock = this.clocks[clockIndex];
            this.addChild(clock);
            let clockX = clock.position.x;
            let clockY = clock.position.y;

            if (LayoutManager.isPortrait) {
                points.push("[" + (symbolX + dY / 2) + "," + symbolY + "]");
                points.push("[" + symbolX + "," + (symbolY + dY / 2) + "]");
                points.push("[" + (symbolX - dY / 2) + "," + symbolY + "]");
                if (options.symbolIndex) {
                    points.push(
                        "[" +
                            (symbolX - dY / 2 + (clockX - symbolX) / 4) +
                            "," +
                            (symbolY + (clockY - symbolY) / 2) +
                            "]",
                    );
                }
                points.push("[" + clockX + "," + clockY + "]");
            } else {
                if (options.reelIndex < 3) {
                    points.push("[" + symbolX + "," + (symbolY - dY / 2) + "]");
                }
                if (options.reelIndex < 4) {
                    points.push("[" + (symbolX + dY / 2) + "," + symbolY + "]");
                }
                points.push("[" + symbolX + "," + (symbolY + dY / 2) + "]");
                if (options.reelIndex) {
                    if (options.symbolIndex < 2) {
                        points.push(
                            "[" +
                                (symbolX - dY / 2 + (clockX - symbolX) / 2) +
                                "," +
                                (symbolY + (clockY - symbolY) / 4) +
                                "]",
                        );
                    } else {
                        points.push(
                            "[" +
                                (symbolX - dY / 2 + (clockX - symbolX) / 2) +
                                "," +
                                (symbolY - dY / 2 + (clockY - symbolY) / 4) +
                                "]",
                        );
                    }
                }
                points.push("[" + clockX + "," + clockY + "]");
            }

            if (this.rope1) {
                this.rope1.setPoints(points.join(";"));
                this.rope1.start();
                this.rope1.visible = true;
                //this.rope1.scale.x = this.rope1.scale.y = LayoutManager.isPortrait ? 0.666 : 1;
            }
            if (this.rope2) {
                this.rope2.setPoints(points.join(";"));
                this.rope2.start();
                this.rope2.visible = true;
                //this.rope2.scale.x = this.rope2.scale.y = LayoutManager.isPortrait ? 0.666 : 1;
            }
            PlayList.play("SND_BonusCounterAdd");
        }

        this.ropeTimer.start(this.ropeDelay);
    }

    clone(): ClockCounter {
        let clone = <ClockCounter>super.clone();
        clone.symbolX = this.symbolX.slice();
        clone.symbolY = this.symbolY.slice();
        return clone;
    }
}

export interface ClockCounterStartOptions {
    currentClocks: number;
    reelIndex: number;
    symbolIndex: number;
    lastSymbolCase?: string;
    winIndex?: number;
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/ClockCounter.ts
 **/